import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";

import { RootState } from "../../../../../../redux/store";
import { setPreviewLanguage } from "../../../../../../redux/scanToSlice";
import { ScanToContext } from "..";
import {
    DiversPreviewData,
    SocialChannel,
} from "../View";

import { PropsType, LANGUAGES, DEFAULT_RESTAURANT_TEXTS, DEFAULT_PRIMARY_COLOR, DEFAULT_SECONDARY_COLOR } from "./types";
import { DEFAULT_SOCIAL_CHANNELS } from "./constants";
import {
    Container,
    LeftSection,
    SectionTitle,
} from "./styled";
import { ColorsCard } from "./ColorsCard";
import { RestaurantInfoCard } from "./RestaurantInfoCard";
import { SocialNetworksCard } from "./SocialNetworksCard";
import { LegalInfoCard } from "./LegalInfoCard";
import { LanguagesCard } from "./LanguagesCard";

// ─── Component ──────────────────────────────────────────

export function PiedDePage({ rowData }: PropsType): JSX.Element {
    const { t } = useTranslation();
    const mainColor =
        useSelector(
            (state: RootState) => state.global.infoUser.maincolor
        ) || DEFAULT_PRIMARY_COLOR;
    const {
        setSharedDiversData,
        broadcastChannelRef,
    } = React.useContext(ScanToContext);

    // ─── State ──────────────────────────────────────────
    // Cast: MiscellaneousConfig type doesn't declare every UI-state field used
    // at runtime (selectedLangRestaurant, restaurantTexts, etc.). Out of scope
    // for ARCH-02.
    const savedDivers = useSelector(
        (state: RootState) => state.scanTo?.miscellaneousSettings
    ) as any;
    const [primaryColor, setPrimaryColor] = React.useState(savedDivers?.primaryColor || DEFAULT_PRIMARY_COLOR);
    const [secondaryColor, setSecondaryColor] = React.useState(savedDivers?.secondaryColor || DEFAULT_SECONDARY_COLOR);
    const [showPrimaryPicker, setShowPrimaryPicker] = React.useState(false);
    const [showSecondaryPicker, setShowSecondaryPicker] = React.useState(false);

    // Restaurant info - multilingual
    // The restaurant-info editing language IS the shared preview language, so
    // the footer preview stays consistent with every other section and picking
    // it here flips the whole preview. Aliased so call sites work unchanged.
    const dispatch = useDispatch();
    const previewLanguage = useSelector(
        (state: RootState) => state.scanTo.previewLanguage
    );
    const selectedLangRestaurant = previewLanguage;
    const setSelectedLangRestaurant = React.useCallback(
        (lang: string) => dispatch(setPreviewLanguage(lang)),
        [dispatch]
    );
    const [restaurantTexts, setRestaurantTexts] = React.useState<Record<string, { name: string; description: string }>>(
        savedDivers?.restaurantTexts || JSON.parse(JSON.stringify(DEFAULT_RESTAURANT_TEXTS))
    );
    const [nameVisible, setNameVisible] = React.useState(savedDivers?.nameVisible ?? true);
    const [descriptionVisible, setDescriptionVisible] = React.useState(savedDivers?.descriptionVisible ?? true);

    const currentTexts = restaurantTexts[selectedLangRestaurant] || { name: "", description: "" };
    const currentDefaults = DEFAULT_RESTAURANT_TEXTS[selectedLangRestaurant] || { name: "", description: "" };

    const [showSocialNetworks, setShowSocialNetworks] = React.useState(savedDivers?.showSocialNetworks ?? true);
    const [socialChannels, setSocialChannels] = React.useState<SocialChannel[]>(() => {
        const saved = savedDivers?.socialChannels;
        if (saved?.length) {
            // API returns { id, url, enabled } — normalize to { key, label, url, enabled }
            return saved.map((ch: any) => ({
                key: ch.key || ch.id || "",
                label: ch.label || DEFAULT_SOCIAL_CHANNELS.find((d) => d.key === (ch.key || ch.id))?.label || ch.key || ch.id || "",
                url: ch.url || "",
                enabled: ch.enabled ?? false,
            }));
        }
        return DEFAULT_SOCIAL_CHANNELS.map((ch) => ({ ...ch }));
    });
    const [showLegalInfo, setShowLegalInfo] = React.useState(savedDivers?.showLegalInfo ?? true);

    // Language settings
    const [enabledLanguages, setEnabledLanguages] = React.useState<Record<string, boolean>>(
        savedDivers?.fullEnabledLanguages || Object.fromEntries(LANGUAGES.map((l) => [l.code, true]))
    );
    const [defaultLanguage, setDefaultLanguage] = React.useState<string>(savedDivers?.defaultLanguage || "fr");

    // ─── Build preview data and sync ────────────────────
    const buildPreviewData = React.useCallback((): DiversPreviewData => {
        const texts = restaurantTexts[selectedLangRestaurant] || { name: "", description: "" };
        return {
            primaryColor,
            secondaryColor,
            restaurantName: texts.name,
            restaurantDescription: texts.description,
            nameVisible,
            descriptionVisible,
            socialChannels: showSocialNetworks ? socialChannels : socialChannels.map((ch) => ({ ...ch, enabled: false })),
            showLegalInfo,
            enabledLanguages: Object.entries(enabledLanguages)
                .filter(([, enabled]) => enabled)
                .map(([code]) => code),
            defaultLanguage,
        };
    }, [primaryColor, secondaryColor, restaurantTexts, selectedLangRestaurant, nameVisible, descriptionVisible, socialChannels, showSocialNetworks, showLegalInfo, enabledLanguages, defaultLanguage]);

    React.useEffect(() => {
        const previewData = {
            ...buildPreviewData(),
            // Full config for Redux persistence
            selectedLangRestaurant,
            restaurantTexts,
            showSocialNetworks,
            fullEnabledLanguages: enabledLanguages,
        };

        // Share with other tabs via context
        setSharedDiversData(previewData);

        // Send to external preview window via the centralized BroadcastChannel
        if (broadcastChannelRef.current) {
            try {
                broadcastChannelRef.current.postMessage({
                    source: "divers",
                    ...previewData,
                });
            } catch (_) {
                /* channel closed */
            }
        }

        // Persist for external window initial load
        sessionStorage.setItem("scanToDiversPreviewData", JSON.stringify(previewData));
    }, [buildPreviewData, broadcastChannelRef, setSharedDiversData, restaurantTexts, showSocialNetworks, enabledLanguages, selectedLangRestaurant]);

    // ─── Handlers ───────────────────────────────────────
    const handleResetColors = () => {
        setPrimaryColor(DEFAULT_PRIMARY_COLOR);
        setSecondaryColor(DEFAULT_SECONDARY_COLOR);
    };

    const handleRestaurantTextChange = (field: "name" | "description", value: string) => {
        setRestaurantTexts((prev) => ({
            ...prev,
            [selectedLangRestaurant]: { ...prev[selectedLangRestaurant], [field]: value },
        }));
    };

    const handleResetRestaurantField = (field: "name" | "description") => {
        const defaults = DEFAULT_RESTAURANT_TEXTS[selectedLangRestaurant];
        if (defaults) {
            setRestaurantTexts((prev) => ({
                ...prev,
                [selectedLangRestaurant]: { ...prev[selectedLangRestaurant], [field]: defaults[field] },
            }));
        }
    };

    const handleResetAllRestaurant = () => {
        setRestaurantTexts(JSON.parse(JSON.stringify(DEFAULT_RESTAURANT_TEXTS)));
        setNameVisible(true);
        setDescriptionVisible(true);
    };

    const handleToggleSocial = (key: string) => {
        setSocialChannels((prev) =>
            prev.map((ch) => (ch.key === key ? { ...ch, enabled: !ch.enabled } : ch))
        );
    };

    const handleSocialUrlChange = (key: string, url: string) => {
        setSocialChannels((prev) =>
            prev.map((ch) => (ch.key === key ? { ...ch, url } : ch))
        );
    };

    const handleToggleLanguage = (code: string) => {
        setEnabledLanguages((prev) => {
            const newState = { ...prev, [code]: !prev[code] };
            // If disabling the default language, switch default to first enabled
            if (!newState[code] && code === defaultLanguage) {
                const firstEnabled = LANGUAGES.find((l) => newState[l.code] && l.code !== code);
                if (firstEnabled) {
                    setDefaultLanguage(firstEnabled.code);
                }
            }
            // Ensure at least one language stays enabled
            const enabledCount = Object.values(newState).filter(Boolean).length;
            if (enabledCount === 0) {
                return prev;
            }
            return newState;
        });
    };

    const handleSetDefaultLanguage = (code: string) => {
        // Enable the language if it's not enabled
        if (!enabledLanguages[code]) {
            setEnabledLanguages((prev) => ({ ...prev, [code]: true }));
        }
        setDefaultLanguage(code);
        // Picking the project default language also drives the shared preview
        // language, so the whole preview switches to it.
        dispatch(setPreviewLanguage(code));
    };

    return (
        <Container data-testid="scan-to-misc-container">
            <LeftSection data-testid="scan-to-misc-left-section">
                <SectionTitle>{t("Miscellaneous settings")}</SectionTitle>

                {/* Card 1 - Colors */}
                <ColorsCard
                    mainColor={mainColor}
                    primaryColor={primaryColor}
                    secondaryColor={secondaryColor}
                    showPrimaryPicker={showPrimaryPicker}
                    showSecondaryPicker={showSecondaryPicker}
                    onTogglePrimaryPicker={() => setShowPrimaryPicker(!showPrimaryPicker)}
                    onToggleSecondaryPicker={() => setShowSecondaryPicker(!showSecondaryPicker)}
                    onPrimaryChange={setPrimaryColor}
                    onSecondaryChange={setSecondaryColor}
                    onClosePrimaryPicker={() => setShowPrimaryPicker(false)}
                    onCloseSecondaryPicker={() => setShowSecondaryPicker(false)}
                    onResetColors={handleResetColors}
                />

                {/* Card 2 - Restaurant Information */}
                <RestaurantInfoCard
                    mainColor={mainColor}
                    selectedLangRestaurant={selectedLangRestaurant}
                    onSelectLangRestaurant={setSelectedLangRestaurant}
                    currentTexts={currentTexts}
                    currentDefaults={currentDefaults}
                    nameVisible={nameVisible}
                    descriptionVisible={descriptionVisible}
                    onRestaurantTextChange={handleRestaurantTextChange}
                    onToggleNameVisible={() => setNameVisible(!nameVisible)}
                    onToggleDescriptionVisible={() => setDescriptionVisible(!descriptionVisible)}
                    onResetRestaurantField={handleResetRestaurantField}
                    onResetAllRestaurant={handleResetAllRestaurant}
                />

                {/* Card 3 - Social Networks */}
                <SocialNetworksCard
                    mainColor={mainColor}
                    showSocialNetworks={showSocialNetworks}
                    onToggleShowSocialNetworks={() => setShowSocialNetworks(!showSocialNetworks)}
                    socialChannels={socialChannels}
                    onToggleSocial={handleToggleSocial}
                    onSocialUrlChange={handleSocialUrlChange}
                />

                {/* Card 4 - Legal Information */}
                <LegalInfoCard
                    mainColor={mainColor}
                    showLegalInfo={showLegalInfo}
                    onToggleShowLegalInfo={() => setShowLegalInfo(!showLegalInfo)}
                />

                {/* Card 5 - Languages */}
                <LanguagesCard
                    mainColor={mainColor}
                    enabledLanguages={enabledLanguages}
                    defaultLanguage={defaultLanguage}
                    onToggleLanguage={handleToggleLanguage}
                    onSetDefaultLanguage={handleSetDefaultLanguage}
                />
            </LeftSection>
        </Container>
    );
}
